import React, { useCallback, useRef } from 'react';

import { type CampaignInfoType, useCampaignCardScale, useIsHeaderCollapsed } from 'features/campaigns';
import { useViewMode } from 'store/app/appStore';
import type { CampaignInfoDisplayStyles } from '../../css/base/CampaignInfoDisplayStyles';
import { getCampaignInfoSuffix } from './CampaignInfoSuffixes';

interface CampaignInfoValueProps {
  type: CampaignInfoType;
  value: string;
  suffix?: string;
  styles: CampaignInfoDisplayStyles;
  attributes: Record<string, string | undefined>;
  externalRef?: React.RefObject<HTMLSpanElement | null>;
}

// clientWidth/offsetWidth round to whole pixels and the budget subtracts three of them, so it
// can read ~1.5px short. On a has-many item (width: max-content) that alone triggers a scale-down
// which then feeds the next measurement. Don't swap in getBoundingClientRect — useCampaignCardScale
// relies on offsetWidth ignoring the carousel's ancestor transform.
const ROUNDING_TOLERANCE_PX = 2;

const CampaignInfoValue: React.FC<CampaignInfoValueProps> = ({ type, value, suffix, styles, attributes, externalRef }) => {
  const isCollapsed = useIsHeaderCollapsed();
  const viewMode = useViewMode();

  // Budget = the item wrapper's own width minus the label (row layout only; in the column layout
  // the label sits on the line above). Never measure from the row above, nor from the value's own
  // width or position — both feed the scale back into its own input (ON-166985/ON-166929).
  const measuredWrapperRef = useRef<HTMLDivElement | null>(null);

  const getContainerWidth = useCallback(() => {
    const wrapper = measuredWrapperRef.current;
    const box = wrapper?.parentElement;

    if (!wrapper || !box) return 0;

    const boxWidth = box.clientWidth;
    if (boxWidth <= 0) return 0;

    if (getComputedStyle(box).flexDirection.startsWith('column')) return boxWidth;

    const label = wrapper.previousElementSibling as HTMLElement | null;
    const gap = parseFloat(getComputedStyle(box).columnGap) || 0;

    return Math.max(boxWidth - (label?.offsetWidth ?? 0) - gap + ROUNDING_TOLERANCE_PX, 1);
  }, []);

  // Expanded cards are scaled as a whole row by BottomCardInfoCampaign; only the collapsed
  // layout, where every item has its own narrow box, still scales item by item.
  const { titleRef: scaleRef, scale, scaledWidth } = useCampaignCardScale(value, {
    recalculateKey: `${viewMode}-${isCollapsed}`,
    getContainerWidth,
    disabled: !isCollapsed,
  });


  // Scale ref on the wrapper, not the span, so the value and its suffix shrink together.
  const wrapperRef = useCallback(
    (el: HTMLDivElement | null) => {
      measuredWrapperRef.current = el;
      (scaleRef as React.RefObject<HTMLDivElement | null>).current = el;
    },
    [scaleRef],
  );

  // The timer updates its value through the span ref.
  const valueRef = useCallback(
    (spanElement: HTMLSpanElement | null) => {
      if (externalRef && 'current' in externalRef) {
        externalRef.current = spanElement;
      }
    },
    [externalRef],
  );

  const SuffixComponent = getCampaignInfoSuffix(type);

  return (
    <div
      ref={wrapperRef}
      className={styles.dynamicValueWrapper}
      {...attributes}
      style={{
        transform: scale < 1 ? `scale(${scale})` : undefined,
        width: scale < 1 && scaledWidth ? `${scaledWidth}px` : undefined,
        transformOrigin: scale < 1 ? 'left bottom' : undefined,
      }}
    >
      <span ref={valueRef} className={styles.dynamicValue} {...attributes}>
        {value}
      </span>
      {SuffixComponent && <SuffixComponent suffix={suffix} attributes={attributes} />}
    </div>
  );
};

export default CampaignInfoValue;
